/**
 * Ordinamento righe vista tabella Piano Turni (condiviso con PDF e altri export).
 * Deve rimanere allineato a ShiftPlanner_v2 · renderWeekTableBody + getSlotTypesForWeek.
 */

import { TIME_SLOTS, isSoMedicinaDoloreSlot } from './types.js';

export const TABLE_VIEW_TIME_SHORT = Object.freeze({
  mattino: 'M',
  pomeriggio: 'P',
  prolungato: 'Pr',
  notte: 'N',
  reperibilita_giorno: 'RG',
  reperibilita_notte: 'RN',
});

export const TABLE_GENERIC_TIME_ORDER = Object.freeze({
  mattino: 0,
  pomeriggio: 1,
  prolungato: 2,
  notte: 3,
  reperibilita_giorno: 4,
  reperibilita_notte: 5,
});

/** Etichetta compatta come in ShiftPlanner (`getCompactSlotLabel`). */
export function getCompactSlotLabel(slot) {
  if (!slot) return '—';
  if (slot.name && String(slot.name).trim()) {
    return String(slot.name).trim();
  }
  const abbr = {
    terapia_intensiva: 'TI',
    sala_operatoria: 'SO',
    nora: 'NORA',
    medicina_dolore: 'MD',
    cure_palliative: 'CP',
    formazione: 'FOR',
    direzione: 'DIR',
  };
  return abbr[slot.category] || String(slot.category || '').replace(/_/g, ' ') || '—';
}

export function isReperibilitaSlotType(st) {
  if (!st) return false;
  const ts = st.timeSlot;
  const at = st.activityType || 'clinico';
  return ts === TIME_SLOTS.REPERIBILITA_GIORNO
    || ts === TIME_SLOTS.REPERIBILITA_NOTTE
    || ts === 'reperibilita'
    || at === 'reperibilita';
}

export function isAnesthesiaAmbulatorySlotType(st) {
  if (!st) return false;
  const cat = String(st.category || '').trim().toLowerCase();
  const name = String(st.name || '').trim().toLowerCase();
  if (cat !== 'sala_operatoria') return false;
  return name.startsWith('amb anest');
}

export function isNoraLikeSalaSlotType(st) {
  if (!st) return false;
  const cat = String(st.category || '').trim().toLowerCase();
  if (cat !== 'sala_operatoria') return false;
  const name = String(st.name || '').trim().toLowerCase();
  return name.startsWith('endo') || name.startsWith('rmn') || name === 'rm';
}

/**
 * Ordine sezioni (dall'alto): TI → SO (std / Amb Anest / NORA-like) → NORA → Formazione
 * → Reperibilità → CP → MD → Direzione → altro.
 */
export function getTableSectionSortKey(st) {
  if (isReperibilitaSlotType(st)) {
    return {
      section: 4,
      timeOrder: {
        reperibilita_giorno: 0,
        reperibilita_notte: 1,
        reperibilita: 0,
      },
    };
  }
  if (isSoMedicinaDoloreSlot(st)) {
    return { section: 6, timeOrder: TABLE_GENERIC_TIME_ORDER };
  }
  const cat = st.category;
  if (cat === 'terapia_intensiva') return { section: 0, timeOrder: TABLE_GENERIC_TIME_ORDER };
  if (cat === 'sala_operatoria') {
    return {
      section: 1,
      subSection: isNoraLikeSalaSlotType(st) ? 2 : (isAnesthesiaAmbulatorySlotType(st) ? 1 : 0),
      timeOrder: TABLE_GENERIC_TIME_ORDER,
    };
  }
  if (cat === 'nora') return { section: 2, timeOrder: TABLE_GENERIC_TIME_ORDER };
  if (cat === 'formazione') return { section: 3, timeOrder: TABLE_GENERIC_TIME_ORDER };
  if (cat === 'cure_palliative') return { section: 5, timeOrder: TABLE_GENERIC_TIME_ORDER };
  if (cat === 'medicina_dolore') return { section: 6, timeOrder: TABLE_GENERIC_TIME_ORDER };
  if (cat === 'direzione') return { section: 7, timeOrder: TABLE_GENERIC_TIME_ORDER };
  return { section: 99, timeOrder: TABLE_GENERIC_TIME_ORDER };
}

/** Chiave riga tabella: nome visivo + fascia abbreviata (come ShiftPlanner). */
export function getPlannerTableRowLabelKey(s) {
  if (!s) return '';
  const name = (s.name && String(s.name).trim()) ? String(s.name).trim() : getCompactSlotLabel({ category: s.category });
  const time = TABLE_VIEW_TIME_SHORT[s.timeSlot] || s.timeSlot || '';
  return `${name}\u001f${time}`;
}

export function getSlotTypeRowLabel(slotType) {
  const name = (slotType.name && slotType.name.trim()) ? slotType.name.trim() : getCompactSlotLabel({ category: slotType.category });
  const time = TABLE_VIEW_TIME_SHORT[slotType.timeSlot] || slotType.timeSlot;
  return { name, time };
}

/**
 * Lista ordinata dei "posti di turno" (una riga per etichetta nome+fascia), come in una settimana Piano Turni.
 * @param {Array<object>} weekSlots — slot del periodo (unione di più settimane ok per PDF).
 */
export function getSlotTypesForWeek(weekSlots) {
  const map = new Map();
  for (const s of weekSlots || []) {
    const labelKey = getPlannerTableRowLabelKey(s);
    if (!map.has(labelKey)) {
      map.set(labelKey, {
        key: labelKey,
        category: s.category,
        name: s.name || '',
        timeSlot: s.timeSlot,
        activityType: s.activityType || 'clinico',
        uocId: s.uocId || '',
      });
    }
  }
  return [...map.values()].sort((a, b) => {
    const sa = getTableSectionSortKey(a);
    const sb = getTableSectionSortKey(b);
    if (sa.section !== sb.section) return sa.section - sb.section;
    if ((sa.subSection || 0) !== (sb.subSection || 0)) return (sa.subSection || 0) - (sb.subSection || 0);
    const ta = sa.timeOrder[a.timeSlot] ?? TABLE_GENERIC_TIME_ORDER[a.timeSlot] ?? 50;
    const tb = sb.timeOrder[b.timeSlot] ?? TABLE_GENERIC_TIME_ORDER[b.timeSlot] ?? 50;
    if (ta !== tb) return ta - tb;
    const { name: na, time: tta } = getSlotTypeRowLabel(a);
    const { name: nb, time: ttb } = getSlotTypeRowLabel(b);
    const parseOrdinal = (name) => {
      const raw = String(name || '').trim();
      const m = raw.match(/^(.*?)(\d+)\s*$/);
      if (!m) return { base: raw, idx: 1 };
      return { base: (m[1] || '').trim(), idx: Number.parseInt(m[2], 10) || 1 };
    };
    const oa = parseOrdinal(na);
    const ob = parseOrdinal(nb);
    const baseCmp = oa.base.localeCompare(ob.base, 'it', { sensitivity: 'base' });
    if (baseCmp !== 0) return baseCmp;
    if (oa.idx !== ob.idx) return oa.idx - ob.idx;
    return `${tta}`.localeCompare(`${ttb}`, 'it');
  });
}
